import { of as observableOf, Observable } from 'rxjs';
import { map, share } from "rxjs/operators";
import { Injectable, EventEmitter, inject } from "@angular/core";
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from "@angular/common/http";
import { CookieService } from "./cookie.service";
import { PrimeNG } from 'primeng/config';
import { ru } from "primelocale/ru.json";
import { en } from "primelocale/en.json";
import { fr } from "primelocale/fr.json";

interface Language {
  code: string;
  discription: string;
}

export class LanguageModel implements Language {
  public code: string;
  public discription: string;

  init(data?: any) {
    if (data) {
      this.code = data["code"];
      this.discription = data["name"];
    }
  }

  static fromJS(data: any): LanguageModel {
    data = typeof data === 'object' ? data : {};
    let result = new LanguageModel();
    result.init(data);
    return result;
  }
}

@Injectable()
export class LanguageService {

  private cultureCookieName = ".AspNetCore.Culture";

  public defaultLanguage: LanguageModel;
  selectLanguage: LanguageModel;

  public onLangChange = new EventEmitter<LanguageModel>();

  languages: LanguageModel[] = [];
  private languagesRequest: Observable<LanguageModel[]>;

  private primeNGConfig: PrimeNG = inject(PrimeNG);

  constructor(public translate: TranslateService, private client: HttpClient,
    private cookieService: CookieService) {
    this.defaultLanguage = new LanguageModel();
    this.defaultLanguage.code = "en-us";
    this.defaultLanguage.discription = "English";

    this.selectLanguage = this.defaultLanguage;

    translate.setDefaultLang(this.defaultLanguage.code);
    translate.onLangChange.subscribe(lang => {
      this.onLangChange.emit(this.selectLanguage);
      this.usePrimeNgLanguage();
      window.dispatchEvent(new CustomEvent("ComponentLoad"));
    });


    this.languagesRequest = this.client.get<string[]>("/ctrl/settings/languages").pipe(map(values => {
      this.languages = [];
      for (let item of values)
        this.languages.push(LanguageModel.fromJS(item));

      let currentCulture = this.getCulture();
      this.selectLanguage = this.languages.find(l => l.code == currentCulture) || this.defaultLanguage;

      return this.languages;
    }), share());


    this.languagesRequest.subscribe(value => {
      this.translate.addLangs(this.languages.map(l => l.code));
      this.translate.use(this.selectLanguage.code);

      this.usePrimeNgLanguage();
    }
    )
  }

  private usePrimeNgLanguage() {
      //TODO:add others languages
    switch (this.selectLanguage.code) {
      case 'ru-ru':
        this.primeNGConfig.setTranslation(ru);
        break;
      case 'en-us':
        this.primeNGConfig.setTranslation(en);
        break;
      case 'fr-fr':
        this.primeNGConfig.setTranslation(fr);
        break;
      default:
        break;
    }
  }

  public changeLanguage(lang: string) {
    this.selectLanguage = this.languages.find(l => l.code == lang) || this.defaultLanguage;
    this.translate.use(this.selectLanguage.code);
    this.cookieService.set(this.cultureCookieName, "c=" + this.selectLanguage.code + "|uic=" + this.selectLanguage.code, 100);
  }

  getLanguage() {
    return this.selectLanguage;
  }

  getLanguages(): Observable<LanguageModel[]> {
    if (this.languages.length == 0)
      return this.languagesRequest;
    return observableOf(this.languages);
  }

  getCulture(): string {
    try {
      let cultureCookie = this.cookieService.get(this.cultureCookieName);
      if (cultureCookie && cultureCookie.length)
        return cultureCookie.split("|")[0].replace("c=", "").toLowerCase();
    }
    catch (ex) { }

    return this.languages.length ? this.languages[0].code : this.defaultLanguage.code;
  }
}
